/**
 * Data hooks for the notifications panel: {@link useNotifications} keeps the
 * unread badge and the notification list in sync (load, mark-one-read,
 * mark-all-read), and {@link usePreferences} loads and mutates the per-channel
 * mute settings. Both talk to the real API and surface a loading/error/ready
 * shape the panel renders from.
 */
import { useCallback, useEffect, useState } from 'react';

import type {
  NotificationPreferenceView,
  NotificationType,
  NotificationView,
} from '../../api/schema';

import { useNotifApi } from './api';

export interface NotificationsState {
  status: 'loading' | 'error' | 'ready';
  items: NotificationView[];
  unread: number;
  reload: () => void;
  markRead: (id: string) => void;
  markAllRead: () => Promise<void>;
}

/**
 * Load the unread count and the recent notifications, and expose the read
 * mutations. Marking read updates optimistically, then re-syncs from the server.
 */
export function useNotifications(limit = 12): NotificationsState {
  const api = useNotifApi();
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [items, setItems] = useState<NotificationView[]>([]);
  const [unread, setUnread] = useState(0);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setStatus('loading');
    void (async () => {
      try {
        const [page, count] = await Promise.all([
          api.list({ limit }, signal),
          api.unreadCount(signal),
        ]);
        if (signal.aborted) return;
        setItems(page.items);
        setUnread(count.unread);
        setStatus('ready');
      } catch {
        if (signal.aborted) return;
        setStatus('error');
      }
    })();
    return () => {
      controller.abort();
    };
  }, [api, limit, nonce]);

  const markRead = useCallback(
    (id: string) => {
      setItems((current) =>
        current.map((n) => (n.id === id && !n.read ? { ...n, read: true } : n)),
      );
      setUnread((u) => Math.max(0, u - 1));
      void api
        .markRead(id)
        .then(() => {
          reload();
        })
        .catch(() => {
          reload();
        });
    },
    [api, reload],
  );

  const markAllRead = useCallback(async () => {
    setItems((current) => current.map((n) => (n.read ? n : { ...n, read: true })));
    setUnread(0);
    try {
      await api.markAllRead();
    } finally {
      reload();
    }
  }, [api, reload]);

  return { status, items, unread, reload, markRead, markAllRead };
}

export interface PreferencesState {
  status: 'loading' | 'error' | 'ready';
  prefs: NotificationPreferenceView;
  saving: boolean;
  toggle: (channel: 'in_app' | 'email', type: NotificationType) => void;
  reload: () => void;
}

const EMPTY_PREFS: NotificationPreferenceView = { muted_in_app: [], muted_email: [] };

function toggleIn(list: NotificationType[], type: NotificationType): NotificationType[] {
  return list.includes(type) ? list.filter((t) => t !== type) : [...list, type];
}

/** Load and mutate the per-channel notification mute preferences. */
export function usePreferences(): PreferencesState {
  const api = useNotifApi();
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [prefs, setPrefs] = useState<NotificationPreferenceView>(EMPTY_PREFS);
  const [saving, setSaving] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setStatus('loading');
    void (async () => {
      try {
        const loaded = await api.preferences(signal);
        if (signal.aborted) return;
        setPrefs(loaded);
        setStatus('ready');
      } catch {
        if (signal.aborted) return;
        setStatus('error');
      }
    })();
    return () => {
      controller.abort();
    };
  }, [api, nonce]);

  const toggle = useCallback(
    (channel: 'in_app' | 'email', type: NotificationType) => {
      const next: NotificationPreferenceView =
        channel === 'in_app'
          ? { ...prefs, muted_in_app: toggleIn(prefs.muted_in_app, type) }
          : { ...prefs, muted_email: toggleIn(prefs.muted_email, type) };
      setPrefs(next);
      setSaving(true);
      void api
        .updatePreferences({ muted_in_app: next.muted_in_app, muted_email: next.muted_email })
        .then((saved) => {
          setPrefs(saved);
        })
        .catch(() => {
          // Revert on failure so the toggle reflects the server's truth.
          setPrefs(prefs);
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [api, prefs],
  );

  return { status, prefs, saving, toggle, reload };
}
